/** IXD Preview DL View 컴포넌트 */

import { useMemo } from "react";
import type { IxdChunk, PeriodType } from "@/apis/compare/route";
import { getPeriodLabel } from "@/utils/ixd-editor/period-labels";
import { getDisplayPeriodOrder } from "@/utils/ixd-editor/period-order";
import { pickLineItemUnitsInRows } from "@/utils/ixd-editor/unit-conversion";
import {
  buildCellIdentityMapFromValues,
  buildCellSchemaTypeMapFromValues,
  buildTableLabelMap,
  getReferenceBalance,
  type CellChangeOrigin,
} from "@/utils/ixd-cell-model";
import type { TableCellColumn } from "../table-cell";
import { TableView } from "./table-view";
import { LineItemUnit } from "@/apis/compare/types";
import { DlViewConfigProvider } from "./dl-view-context";
import { TableUnitCaption } from "@/components/common/table-unit-caption";

const EMPTY_ACTIVE_TABLE: Record<
  PeriodType,
  Array<{ columns: TableCellColumn[] }>
> = {
  CURRENT: [],
  PREVIOUS: [],
  BEFORE_PREVIOUS: [],
};

interface DlViewProps {
  dlView: {
    title: string;
    table: Record<PeriodType, Array<{ columns: TableCellColumn[] }>> | null;
    isTransposed: boolean;
    periodLabels?: IxdChunk["dlViews"][number]["periodLabels"];
    fsType?: IxdChunk["dlViews"][number]["fsType"];
    unitText?: string | null;
    lineItemUnits?: Record<string, LineItemUnit>;
    values?: IxdChunk["dlViews"][number]["values"];
  };
  /** DL(role) id — 셀을 (DL, cell) 단위로 스코프할 때 컨테이너에 data-dl-id로 노출 */
  dlId?: string | null;
  /** 스크롤 타깃용 id (예: chunkId-dl-0) */
  sectionId?: string;
  /** 연동 계정 위치와 대조할 원본 섹션 id */
  identitySectionId?: string;
  hideBeforePreviousPeriod?: boolean;
  showPeriodLabel?: boolean;
  cellChanges?: Record<string, string>;
  changeOrigins?: Record<string, CellChangeOrigin>;
  cellIdentityByNormalizedCellId?: Map<string, string>;
  identityChanges?: Map<string, string>;
  onCellEdit?: (
    cellId: string,
    newValue: string,
    identityKey?: string,
    locationUid?: string,
  ) => void;
  unitDivisor?: number;
}

export const DlViewSeparator = () => {
  return <div className="w-full h-px bg-line-normal" aria-hidden="true" />;
};

export const DlView = ({
  dlView,
  dlId,
  sectionId,
  identitySectionId,
  hideBeforePreviousPeriod,
  showPeriodLabel = true,
  cellChanges,
  changeOrigins,
  cellIdentityByNormalizedCellId,
  identityChanges,
  onCellEdit,
  unitDivisor,
}: DlViewProps) => {
  const activeTable = dlView.table ?? EMPTY_ACTIVE_TABLE;
  const dlViewIdentityByNormalizedCellId = useMemo(
    () => buildCellIdentityMapFromValues(dlView.values),
    [dlView.values],
  );
  const schemaTypeByNormalizedCellId = useMemo(
    () => buildCellSchemaTypeMapFromValues(dlView.values),
    [dlView.values],
  );

  const periodTypes = useMemo(
    () =>
      getDisplayPeriodOrder(dlView.fsType).filter(
        (pt) =>
          activeTable[pt] &&
          (!hideBeforePreviousPeriod || pt !== "BEFORE_PREVIOUS"),
      ),
    [dlView.fsType, activeTable, hideBeforePreviousPeriod],
  );

  const firstPeriodType = periodTypes[0];
  const firstPeriodRows = useMemo(
    () => (firstPeriodType ? (activeTable[firstPeriodType] ?? []) : []),
    [activeTable, firstPeriodType],
  );
  const isTransposed = dlView.isTransposed;
  const referenceBalance = getReferenceBalance(firstPeriodRows);

  // 캡션은 표에 그려지는 기간의 셀만 본다 — DL 전체 단위 맵에는 이 표에 없는 태그도 있다.
  const visibleForeignUnits = useMemo(
    () =>
      pickLineItemUnitsInRows(
        dlView.lineItemUnits,
        periodTypes.flatMap((pt) => activeTable[pt]),
      ),
    [dlView.lineItemUnits, periodTypes, activeTable],
  );

  const labelMap = useMemo(() => {
    return buildTableLabelMap(firstPeriodRows);
  }, [firstPeriodRows]);

  return (
    <DlViewConfigProvider
      dlViewIdentityByNormalizedCellId={dlViewIdentityByNormalizedCellId}
      schemaTypeByNormalizedCellId={schemaTypeByNormalizedCellId}
      referenceBalance={referenceBalance}
      labelMap={labelMap}
      isTransposed={isTransposed}
      lineItemUnits={dlView.lineItemUnits}
      sectionId={sectionId}
      identitySectionId={identitySectionId}
      showPeriodLabel={showPeriodLabel}
      unitDivisor={unitDivisor}
    >
      <div
        {...(sectionId ? { id: sectionId } : {})}
        {...(dlId ? { "data-dl-id": dlId } : {})}
        className="flex flex-col gap-[0.75rem]"
      >
        <div data-ixd-dl-header className="flex flex-col gap-0.5">
          <div className="label-2-medium text-label-normal">{dlView.title}</div>
          <TableUnitCaption
            unitText={dlView.unitText}
            foreignUnits={visibleForeignUnits}
          />
        </div>

        {periodTypes.length > 0 && (
          <div className="flex flex-col gap-[0.75rem]">
            {periodTypes.map((periodType) => (
              <TableView
                key={periodType}
                periodType={periodType}
                periodLabel={getPeriodLabel(periodType, dlView.periodLabels)}
                rows={activeTable[periodType]}
                cellChanges={cellChanges}
                changeOrigins={changeOrigins}
                cellIdentityByNormalizedCellId={cellIdentityByNormalizedCellId}
                identityChanges={identityChanges}
                onCellEdit={onCellEdit}
              />
            ))}
          </div>
        )}
      </div>
    </DlViewConfigProvider>
  );
};
